import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * Desktop media query used for the stacked layout mode.
   * @type {MediaQueryList|undefined}
   */
  #desktopMedia = undefined;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Tracks sticky announcement height changes.
   * @type {ResizeObserver|undefined}
   */
  #resizeObserver = undefined;

  /**
   * Observes sticky state changes on the header component.
   * @type {MutationObserver|undefined}
   */
  #headerObserver = undefined;

  /**
   * Watches the document until the header component is available.
   * @type {MutationObserver|undefined}
   */
  #documentObserver = undefined;

  /**
   * The header component currently being observed.
   * @type {HTMLElement|undefined}
   */
  #observedHeader = undefined;

  /**
   * Last value written for each body-level CSS custom property, so unchanged
   * values can be skipped. These are set on document.body and are read by the
   * header and by product-information-content, so every write invalidates style
   * across a large subtree — and #syncStickyState runs on every resize and
   * header mutation.
   * @type {Map<string, number>}
   */
  #writtenOffsets = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    if (this.stackedOnDesktop || this.stickyMode === 'desktop') {
      this.#desktopMedia = window.matchMedia('(min-width: 750px)');
      this.#desktopMedia.addEventListener('change', this.#handleDesktopMediaChange);
    }

    if (this.stickyMode !== 'disabled' && this.sectionContainer instanceof HTMLElement) {
      // The observer watches the same element this callback measures, so take the
      // height from the entry rather than reading offsetHeight back off the DOM.
      this.#resizeObserver = new ResizeObserver((entries) => {
        const blockSize = entries[entries.length - 1]?.borderBoxSize?.[0]?.blockSize;
        // Rounded to match offsetHeight, which the other callers use — otherwise a
        // fractional entry and a rounded read disagree and each keeps re-writing.
        this.#syncStickyState(blockSize == null ? undefined : Math.round(blockSize));
      });
      this.#resizeObserver.observe(this.sectionContainer);
    }

    this.#watchHeaderState();
    this.#syncLayoutMode();
    this.#syncStickyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#desktopMedia?.removeEventListener('change', this.#handleDesktopMediaChange);
    this.#resizeObserver?.disconnect();
    this.#headerObserver?.disconnect();
    this.#documentObserver?.disconnect();
    this.#observedHeader = undefined;

    this.suspend();
    this.sectionContainer?.removeAttribute('data-sticky-active');
    this.#clearStickyOffset();
    // Cleared here rather than in #clearStickyOffset(): that helper also runs in the live
    // 'not pinned' path, where --header-slide-offset has just been set for an in-flight
    // idle/hidden slide and must survive. Only a genuine teardown should retract it, or the
    // header is left pinning against the offset of a bar that no longer exists.
    document.body.style.removeProperty('--header-slide-offset');
    // Both properties are off the body now, so drop the write cache or a
    // reconnect would skip re-establishing a value it thinks is already set.
    this.#writtenOffsets.clear();
  }

  next() {
    if (this.isDesktopStacked) return;

    this.current += 1;
  }

  previous() {
    if (this.isDesktopStacked) return;

    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;
    if (this.isDesktopStacked) return;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;
    if (this.isDesktopStacked) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get stackedOnDesktop() {
    return this.hasAttribute('stacked-on-desktop');
  }

  get stickyMode() {
    return this.getAttribute('sticky-mode') ?? 'disabled';
  }

  get headerComponent() {
    const header = document.querySelector('header-component');
    return header instanceof HTMLElement ? header : undefined;
  }

  get sectionContainer() {
    return this.closest('.announcement-bar');
  }

  get isDesktopStacked() {
    return this.stackedOnDesktop && Boolean(this.#desktopMedia?.matches);
  }

  get isStickyAllowedBySetting() {
    if (this.stickyMode === 'always') return true;
    if (this.stickyMode === 'desktop') return Boolean(this.#desktopMedia?.matches);

    return false;
  }

  get isHeaderStickyActive() {
    const header = this.headerComponent;
    if (!(header instanceof HTMLElement)) return false;

    const stickyMode = header.getAttribute('sticky');
    if (stickyMode === 'always') return true;
    if (stickyMode === 'scroll-up') return header.dataset.stickyState === 'active';

    return false;
  }

  get isStickyActive() {
    return this.isStickyAllowedBySetting && this.isHeaderStickyActive;
  }

  /**
   * Whether this bar's height is currently part of the header's sticky geometry.
   *
   * Deliberately broader than `isStickyActive`, and kept separate from it. `isStickyActive`
   * answers "is the bar pinned right now", which is what --sticky-announcement-height means
   * and what other components (the product page's sticky offsets) read it for. This answers
   * "does the header need to know the bar's height", which stays true while the header is
   * hidden as well: the header pins beneath the bar, so it must travel that far to clear the
   * viewport, and must start its slide back in from there. Publishing the bar's height as
   * "pinned" while it isn't would offset the product page against a bar nobody can see.
   *
   * @returns {boolean}
   */
  get isHeaderSlideOffsetNeeded() {
    const header = this.headerComponent;
    if (!(header instanceof HTMLElement)) return false;

    const stickyMode = header.getAttribute('sticky');
    if (stickyMode === 'always') return true;
    if (stickyMode === 'scroll-up') {
      return ['active', 'idle', 'hidden'].includes(header.dataset.stickyState ?? '');
    }

    return false;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    if (this.isDesktopStacked) {
      this.#showAllSlides();
      return;
    }

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #handleDesktopMediaChange = () => {
    this.#syncLayoutMode();
    this.#syncStickyState();
  };

  #syncLayoutMode() {
    this.suspend();

    if (this.isDesktopStacked) {
      this.#showAllSlides();
      return;
    }

    this.current = this.current;
    this.play();
  }

  #showAllSlides() {
    this.refs.slides?.forEach((slide) => {
      slide.setAttribute('aria-hidden', 'false');
    });
  }

  #watchHeaderState() {
    if (this.stickyMode === 'disabled') {
      this.#headerObserver?.disconnect();
      this.#documentObserver?.disconnect();
      this.#observedHeader = undefined;
      return;
    }

    const header = this.headerComponent;
    if (header instanceof HTMLElement) {
      this.#observeHeaderState(header);
      return;
    }

    if (this.#documentObserver) return;

    this.#documentObserver = new MutationObserver(() => {
      const nextHeader = this.headerComponent;
      if (!(nextHeader instanceof HTMLElement)) return;

      this.#observeHeaderState(nextHeader);
      this.#documentObserver?.disconnect();
      this.#documentObserver = undefined;
    });

    this.#documentObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * @param {HTMLElement} header
   */
  #observeHeaderState(header) {
    if (this.#observedHeader === header) {
      this.#syncStickyState();
      return;
    }

    this.#headerObserver?.disconnect();
    this.#observedHeader = header;
    this.#headerObserver = new MutationObserver(() => this.#syncStickyState());
    this.#headerObserver.observe(header, {
      attributes: true,
      attributeFilter: ['sticky', 'data-sticky-state'],
    });

    this.#syncStickyState();
  }

  /**
   * @param {number} [measuredHeight] - Border-box height from a ResizeObserver
   *   entry. Omitted by the mutation and direct callers, which fall back to
   *   reading the DOM.
   */
  #syncStickyState(measuredHeight) {
    const stickyActive = this.isStickyActive;
    this.sectionContainer?.toggleAttribute('data-sticky-active', stickyActive);

    const height = measuredHeight ?? this.sectionContainer?.offsetHeight ?? 0;
    const slideOffset = this.isStickyAllowedBySetting && this.isHeaderSlideOffsetNeeded ? height : 0;
    this.#writeOffset('--header-slide-offset', slideOffset);

    if (!stickyActive) {
      this.#clearStickyOffset();
      return;
    }

    this.#writeOffset('--sticky-announcement-height', height);
  }

  /**
   * Writes a body-level offset only when it actually changed. --header-slide-offset
   * feeds the header's own top/transform, so an unconditional write here could
   * resize this bar and re-trigger the observer that called us.
   * @param {string} name
   * @param {number} value
   */
  #writeOffset(name, value) {
    if (this.#writtenOffsets.get(name) === value) return;
    this.#writtenOffsets.set(name, value);
    document.body.style.setProperty(name, `${value}px`);
  }

  #clearStickyOffset() {
    this.#writeOffset('--sticky-announcement-height', 0);
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
